// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Profile page  (building blocks of pages/Account.tsx)
// Section (a titled card of rows) and Row (one tappable line: icon, title, optional badge, description, chevron).
// A Row is a router <Link> when given `to`, else a <button> (for actions such as opening cookie preferences).
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, type LucideIcon } from "lucide-react";

export function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  const id = `section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h2 id={id} className="font-display text-base font-bold text-ocean-950 dark:text-foam-50">{title}</h2>
        {hint && <span className="text-xs font-semibold text-ocean-800/70 dark:text-foam-100/65">{hint}</span>}
      </div>
      <ul className="divide-y divide-ocean-900/5 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:divide-white/10 dark:bg-ocean-900 dark:ring-white/10">{children}</ul>
    </section>
  );
}

interface RowProps {
  icon: LucideIcon;
  title: string;
  description: string;
  badge?: string;
  to?: string;
  onClick?: () => void;
}

export function Row({ icon: Icon, title, description, badge, to, onClick }: RowProps) {
  const body = (
    <>
      <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ocean-950 dark:text-foam-50">{title}</span>
          {badge && <span className="rounded-full bg-ocean-700/10 px-2 py-0.5 text-xs font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">{badge}</span>}
        </span>
        <span className="block text-sm text-ocean-800/80 dark:text-foam-100/75">{description}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-ocean-800/60 dark:text-foam-100/60" aria-hidden="true" />
    </>
  );
  const cls =
    "flex min-h-16 w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-ocean-700/5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-teal-600 dark:hover:bg-white/5";
  return (
    <li>
      {to ? (
        <Link to={to} className={cls}>{body}</Link>
      ) : (
        <button type="button" onClick={onClick} className={cls}>{body}</button>
      )}
    </li>
  );
}
